export function animateValue(
  element: HTMLElement,
  from: number,
  to: number,
  duration: number,
  formatter?: (value: number) => string
): void {
  const startTime = Date.now()
  const delta = to - from
  
  const animate = () => {
    const now = Date.now()
    const progress = Math.min((now - startTime) / duration, 1)
    
    // Easing function (ease-out-cubic)
    const easeProgress = 1 - Math.pow(1 - progress, 3)
    const currentValue = from + (delta * easeProgress)
    
    if (formatter) {
      element.textContent = formatter(currentValue)
    } else {
      element.textContent = currentValue.toFixed(2)
    }
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    }
  }
  
  requestAnimationFrame(animate)
}

export function fadeIn(element: HTMLElement, duration = 300): void {
  element.style.opacity = '0'
  element.style.transition = `opacity ${duration}ms ease-in-out`
  
  // Force reflow
  element.offsetHeight
  
  element.style.opacity = '1'
}

export function fadeOut(element: HTMLElement, duration = 300): Promise<void> {
  return new Promise(resolve => {
    element.style.transition = `opacity ${duration}ms ease-in-out`
    element.style.opacity = '0'
    
    setTimeout(() => {
      resolve()
    }, duration)
  })
}

export function slideIn(
  element: HTMLElement,
  direction: 'top' | 'right' | 'bottom' | 'left',
  duration = 500
): void {
  const transforms = {
    top: 'translateY(-100%)',
    right: 'translateX(100%)',
    bottom: 'translateY(100%)',
    left: 'translateX(-100%)'
  }
  
  element.style.transform = transforms[direction]
  element.style.transition = `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`
  
  // Force reflow
  element.offsetHeight
  
  element.style.transform = 'translate(0, 0)'
}

export function pulse(element: HTMLElement, duration = 1000): void {
  element.style.animation = `pulse ${duration}ms ease-in-out`
  
  setTimeout(() => {
    element.style.animation = ''
  }, duration)
}

export class AnimationController {
  private animations: Map<string, number> = new Map()
  
  public start(id: string, callback: () => void, _interval: number): void {
    this.stop(id)
    
    const animate = () => {
      callback()
      this.animations.set(id, requestAnimationFrame(animate))
    }
    
    this.animations.set(id, requestAnimationFrame(animate))
  }
  
  public stop(id: string): void {
    const animationId = this.animations.get(id)
    if (animationId) {
      cancelAnimationFrame(animationId)
      this.animations.delete(id)
    }
  }
  
  public stopAll(): void {
    this.animations.forEach((_, id) => this.stop(id))
  }
}

export function springAnimation(
  from: number,
  to: number,
  options: {
    stiffness?: number
    damping?: number
    mass?: number
    onUpdate: (value: number) => void
    onComplete?: () => void
  }
): () => void {
  const { stiffness = 100, damping = 10, mass = 1, onUpdate, onComplete } = options
  
  let position = from
  let velocity = 0
  let animationId: number
  
  const animate = () => {
    const force = -(stiffness * (position - to))
    const damper = -damping * velocity
    const acceleration = (force + damper) / mass
    
    velocity += acceleration * 0.016 // Assuming 60fps
    position += velocity * 0.016
    
    onUpdate(position)
    
    // Check if animation is complete
    if (Math.abs(velocity) < 0.001 && Math.abs(position - to) < 0.001) {
      onUpdate(to)
      onComplete?.()
    } else {
      animationId = requestAnimationFrame(animate)
    }
  }
  
  animationId = requestAnimationFrame(animate)
  
  // Return cleanup function
  return () => {
    if (animationId) {
      cancelAnimationFrame(animationId)
    }
  }
}